import React, { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { CentroServiceConfigQry, PackageWorkflowServiceModel, ServiceLevel } from 'app/modules/service-management/models'
import { mockServiceApi } from '../../mock/mockServiceData'
import {toast} from 'react-toastify'
import {useAppDispatch, useAppSelector} from 'setup/redux/Hooks'
import {ToastDefaultConfig} from 'setup/toast/ToastConfig'
import {selectUserMetaData} from 'app/modules/auth'
import {Modal} from 'react-bootstrap-v5'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'app/utils/helpers'
import {
  CollapsibleSection,
  FORM_ICONS,
  FieldLabel,
  SelectField,
  TextAreaField,
  TextField,
  ToggleRow,
  VatTreatment,
  VatTreatmentField,
} from './ServiceFormFields'
import AdvancedPricingSettingsModal, {CityPriceConfig} from './AdvancedPricingSettingsModal'
import './style.scss'
import { getApiErrorMessage } from '../../utils/apiError'
import { formatMoneyText, parseMoneyText, roundMoney, sanitizeMoneyText } from '../../utils/money'
import { stripIconSasToken } from '../../utils/serviceIcon'
import { serviceManagementAction } from '../../redux/ServiceManagementSlice'
import { fetchInheritedDuration, InheritedDuration } from '../../utils/inheritedDuration'

interface AddSubServiceModalProps {
  isShow: boolean
  onHide: () => void
  // The same form serves level 2 and level 3; only the parent ids differ
  serviceLevel: ServiceLevel
  categoryInfoId: string
  sourceType?: string
  onSaved: () => void
  onSave?: () => void
  isLoading?: boolean
  parentServiceId?: number
  parentSubServiceId?: number
  mode?: 'add' | 'edit'
  nodeId?: string
  serviceId?: number
  isCustom?: boolean
  initialNameEn?: string
  initialNameAr?: string
  initialStatus?: boolean
  // Edit only: the B8ak source is gone, so only Category and Description stay editable.
  isRemovedFromB8ak?: boolean
  statusLockMessage?: string
  // B8ak Maintenance shows Workers/recurring as 1 when unset; the API can't classify Maintenance yet.
  isMaintenanceService?: boolean
  // B8ak only: the service tree's values for this node, shown in the locked fields when it has no own value
  catalogueDuration?: number | null
  catalogueWorkersNeeded?: number | null
}

const toFieldText = (value?: number | null) => (value == null ? '' : String(value))

type SubServiceFormValues = PackageWorkflowServiceModel & {
  visitDuration?: number
  numberOfWorkers?: number
  price?: number
  priceWithVat?: number
  parentServiceId?: number
}

const AddSubServiceModal: React.FC<AddSubServiceModalProps> = ({
  isShow,
  onHide,
  serviceLevel,
  categoryInfoId,
  onSaved,
  onSave,
  isLoading = false,
  parentServiceId,
  parentSubServiceId,
  mode = 'add',
  nodeId,
  serviceId,
  isCustom = true,
  initialNameEn,
  initialNameAr,
  initialStatus,
  isRemovedFromB8ak = false,
  statusLockMessage,
  sourceType,
  isMaintenanceService = false,
  catalogueDuration,
  catalogueWorkersNeeded,
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isLoadingConfig, setIsLoadingConfig] = useState<boolean>(false)
  const userMetaData = useAppSelector(selectUserMetaData)
  const vatPercent = (userMetaData?.vatValue || 0) / 100
  const [vatTreatment, setVatTreatment] = useState<VatTreatment>('excl')
  const [priceText, setPriceText] = useState<string>('')
  const [isAdvancedPricingOpen, setIsAdvancedPricingOpen] = useState(false)
  const [cityPrices, setCityPrices] = useState<CityPriceConfig[]>([])

  const [isRecurringOpen, setIsRecurringOpen] = useState(false)
  const [isReschedulingOpen, setIsReschedulingOpen] = useState(false)
  const [packageWeeks, setPackageWeeks] = useState<number | undefined>(1)
  const [weeklyVisits, setWeeklyVisits] = useState<number | undefined>(1)
  const [rescheduleWindowDays, setRescheduleWindowDays] = useState<number | undefined>(7)
  const [rescheduleBeforeMinutes, setRescheduleBeforeMinutes] = useState<number | undefined>(720)
  const [rescheduleLimit, setRescheduleLimit] = useState<number | undefined>(2)
  const [oneTimeForAllDays, setOneTimeForAllDays] = useState(false)
  const [enableRescheduling, setEnableRescheduling] = useState(false)
  const [rescheduleFeeText, setRescheduleFeeText] = useState('')
  const [rescheduleFeeExcl, setRescheduleFeeExcl] = useState<number | undefined>(undefined)
  const [rescheduleFeeIncl, setRescheduleFeeIncl] = useState<number | undefined>(undefined)
  const [rescheduleVat, setRescheduleVat] = useState<VatTreatment>('excl')
  const [receiveB8akOrders, setReceiveB8akOrders] = useState(false)
  const [shiftType, setShiftType] = useState<number>(0)
  const [inheritedDuration, setInheritedDuration] = useState<InheritedDuration | null>(null)

  const isArabic = i18n.language?.toLowerCase().startsWith('ar')

  const isEdit = mode === 'edit'
  // A B8ak service takes its name, duration, workers and price from B8ak;
  // the provider only edits descriptions and the Centro status.
  const isB8akService = isEdit && !isCustom
  const showMaintenanceDefaults = isB8akService && isMaintenanceService

  const hasParentServiceId = Number.isFinite(Number(parentServiceId))
  const hasParentSubServiceId = Number.isFinite(Number(parentSubServiceId))
  const isParentReady =
    isEdit || (hasParentServiceId && (serviceLevel !== 3 || hasParentSubServiceId))

  const initialValues: SubServiceFormValues = useMemo(
    () => ({
      id: 0,
      nameAr: '',
      nameEn: '',
      subTitleTextEn: '',
      subTitleTextAr: '',
      svgIcon: '',
      icon: '' as any,

      status: 0,
      published: 0, // <-- thêm field này

      packageWorkFlowId: 0,

      visitDuration: undefined,
      numberOfWorkers: undefined,
      price: undefined,
      priceWithVat: undefined,

      parentServiceId,
    }),
    [parentServiceId]
  )

  // Sub-Service rule: names are required. Duration, Workers Needed and Price
  // are optional; empty values inherit from the nearest parent.
  // Read-only B8ak fields cannot be corrected by the provider, so they are not validated.
  const validationSchema = isB8akService
    ? Yup.object({})
    : Yup.object({
      nameEn: Yup.string()
        .trim()
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_EN_REQUIRED')),
      nameAr: Yup.string()
        .trim()
        .required(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_AR_REQUIRED')),
      visitDuration: Yup.number()
        .nullable()
        .integer(t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_INVALID'))
        .moreThan(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_MIN')),
      numberOfWorkers: Yup.number()
        .nullable()
        .moreThan(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WORKERS_MIN')),
      price: Yup.number().nullable().min(0, t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE_MIN')),
    })

  const formik = useFormik<SubServiceFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (formValues) => {
      if (!isParentReady) {
        toast.error(
          t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PARENT_NOT_READY'),
          ToastDefaultConfig()
        )
        return
      }

      const isIncl = vatTreatment === 'incl'
      const typedPrice = isIncl ? formValues.priceWithVat : formValues.price
      const isRescheduleFeeIncl = rescheduleVat === 'incl'
      const typedRescheduleFee = isRescheduleFeeIncl ? rescheduleFeeIncl : rescheduleFeeExcl
      const nameAr = formValues.nameAr.trim()
      const nameEn = formValues.nameEn.trim()

      const shared = {
        status: Boolean(formValues.status),
        duration: formValues.visitDuration ?? null,
        workersNeeded: formValues.numberOfWorkers ?? null,
        price: typedPrice ?? null,
        vatTreatment: typedPrice === undefined ? null : isIncl ? 1 : 0,
        descriptionAr: formValues.subTitleTextAr || null,
        descriptionEn: formValues.subTitleTextEn || null,
        icon: stripIconSasToken(typeof formValues.icon === 'string' ? formValues.icon : null),
      }

      const packageFields = {
        packageWeeks,
        weeklyVisits,
        rescheduleWindowDays,
        rescheduleBeforeMinutes,
        rescheduleLimit,
        oneTimeForAllDays,
        enableRescheduling,
        rescheduleFeeAmount: typedRescheduleFee ?? null,
        rescheduleVatTreatment: typedRescheduleFee === undefined ? null : isRescheduleFeeIncl ? 1 : 0,
        shiftType,
      }

      const done = (res: unknown, err: unknown) => {
        setIsSaving(false)
        if (err || !res) {
          toast.error(getApiErrorMessage(err, t('COMMON.PLEASE_TRY_AGAIN')), ToastDefaultConfig())
          return
        }
        toast.success(
          t(
            isEdit
              ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_UPDATED'
              : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_ADDED'
          ),
          ToastDefaultConfig()
        )
        onSaved()
        onSave?.()
        onHide()
      }

      setIsSaving(true)

      if (!isEdit) {
        dispatch(
          serviceManagementAction.addCentroCategoryService({
            data: {
              categoryInfoId,
              serviceLevel,
              serviceId: Number(parentServiceId),
              subServiceId: serviceLevel === 3 ? Number(parentSubServiceId) : null,
              nameAr,
              nameEn,
              ...shared,
              ...packageFields,
            },
            callback: done,
          })
        )
        return
      }

      if (isCustom && nodeId) {
        dispatch(
          serviceManagementAction.updateCentroCategoryService({
            data: { id: nodeId, body: { serviceLevel, nameAr, nameEn, ...shared, ...packageFields } },
            callback: done,
          })
        )
        return
      }

      // A B8ak node keeps its catalogue name; only the company's configuration is saved.
      dispatch(
        serviceManagementAction.saveCentroServiceConfig({
          data: {
            categoryInfoId,
            serviceLevel,
            serviceId: Number(serviceId),
            ...shared,
            receiveB8akOrders,
          },
          callback: done,
        })
      )
    },
  })

  const { values, errors, touched, isValid, setFieldValue, setFieldTouched, submitForm, resetForm } =
    formik

  useEffect(() => {
    if (!isShow) return

    const baseValues: SubServiceFormValues = {
      ...initialValues,
      nameEn: initialNameEn ?? initialValues.nameEn,
      nameAr: initialNameAr ?? initialValues.nameAr,
      status: initialStatus === undefined ? initialValues.status : initialStatus ? 1 : 0,
    }

    resetForm({ values: baseValues })
    setVatTreatment('excl')
    setPriceText('')
    setCityPrices([])
    setIsAdvancedPricingOpen(false)
    setIsRecurringOpen(false)
    setIsReschedulingOpen(false)
    setPackageWeeks(1)
    setWeeklyVisits(1)
    setRescheduleWindowDays(7)
    setRescheduleBeforeMinutes(720)
    setRescheduleLimit(2)
    setOneTimeForAllDays(false)
    setEnableRescheduling(false)
    setRescheduleFeeText('')
    setRescheduleVat('excl')
    setReceiveB8akOrders(false)
    setShiftType(0)

    if (!isEdit || !Number.isFinite(Number(serviceId))) return

    setIsLoadingConfig(true)
    dispatch(
      serviceManagementAction.getCentroServiceConfig({
        
        data: { serviceLevel, serviceId: Number(serviceId), categoryInfoId },
        callback: (res: CentroServiceConfigQry | null, err: unknown) => {
          setIsLoadingConfig(false)

          if (err) {
            toast.error(
              getApiErrorMessage(err, t('COMMON.PLEASE_TRY_AGAIN')),
              ToastDefaultConfig()
            )
            return
          }

          if (!res) return

          const isIncl = res.vatTreatment === 1
          const savedPrice = res.price ?? undefined

          const priceExcl =
            savedPrice === undefined
              ? undefined
              : roundMoney(isIncl ? savedPrice / (1 + vatPercent) : savedPrice)
          const priceIncl =
            savedPrice === undefined
              ? undefined
              : roundMoney(isIncl ? savedPrice : savedPrice * (1 + vatPercent))

          setVatTreatment(isIncl ? 'incl' : 'excl')
          setPriceText(formatMoneyText(isIncl ? priceIncl : priceExcl))
          resetForm({
            values: {
              ...baseValues,
              visitDuration: res.duration ?? undefined,
              numberOfWorkers: res.workersNeeded ?? undefined,
              price: priceExcl,
              priceWithVat: priceIncl,
              status: res.status == null ? baseValues.status : res.status ? 1 : 0,
              subTitleTextEn: res.descriptionEn ?? baseValues.subTitleTextEn,
              subTitleTextAr: res.descriptionAr ?? baseValues.subTitleTextAr,
              icon: (res.icon ?? baseValues.icon) as any,
            },
          })
          setPackageWeeks(isB8akService ? res.packageWeeks ?? undefined : res.packageWeeks ?? 1)
          setWeeklyVisits(isB8akService ? res.weeklyVisits ?? undefined : res.weeklyVisits ?? 1)
          setRescheduleWindowDays(res.rescheduleWindowDays ?? undefined)
          setRescheduleBeforeMinutes(res.rescheduleBeforeMinutes ?? undefined)
          setRescheduleLimit(res.rescheduleLimit ?? undefined)
          setOneTimeForAllDays(Boolean(res.oneTimeForAllDays))
          setEnableRescheduling(Boolean(res.enableRescheduling))
          const isRescheduleFeeIncl = res.rescheduleVatTreatment === 1
          const savedFee = res.rescheduleFeeAmount ?? undefined
          const feeExcl =
            savedFee === undefined
              ? undefined
              : roundMoney(isRescheduleFeeIncl ? savedFee / (1 + vatPercent) : savedFee)
          const feeIncl =
            savedFee === undefined
              ? undefined
              : roundMoney(isRescheduleFeeIncl ? savedFee : savedFee * (1 + vatPercent))
          setRescheduleFeeExcl(feeExcl)
          setRescheduleFeeIncl(feeIncl)
          setRescheduleVat(isRescheduleFeeIncl ? 'incl' : 'excl')
          setRescheduleFeeText(formatMoneyText(isRescheduleFeeIncl ? feeIncl : feeExcl))
          setReceiveB8akOrders(Boolean(res.receiveB8akOrders))
          setShiftType(res.shiftType ?? 0)
        },
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow, isEdit, serviceLevel, serviceId, initialValues])

  useEffect(() => {
    setInheritedDuration(null)
    if (!isShow || isB8akService) return

    let isCurrent = true
    fetchInheritedDuration(categoryInfoId, [{serviceLevel: 1, serviceId: parentServiceId}])
      .then((result) => {
        if (isCurrent) setInheritedDuration(result)
      })
      .catch(() => undefined)
    return () => {
      isCurrent = false
    }
  }, [isShow, isB8akService, categoryInfoId, parentServiceId])

  // Inherited Duration is only a placeholder - never written into the form, so it is never saved.
  const durationPlaceholder = isB8akService
    ? undefined
    : inheritedDuration
    ? String(inheritedDuration.minutes)
    : '30'
  const durationHint =
    !isB8akService && values.visitDuration === undefined && inheritedDuration
      ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION_INHERITED_FROM_MAJOR', {
          minutes: inheritedDuration.minutes,
        })
      : undefined

  const toOptionalNumber = (raw: string) => {
    if (raw.trim() === '') return undefined
    const parsed = Number(raw)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  const handlePriceChange = (raw: string) => {
    const text = sanitizeMoneyText(raw)
    setPriceText(text)

    const entered = parseMoneyText(text)
    if (entered === null) {
      setFieldValue('price', undefined, true)
      setFieldValue('priceWithVat', undefined, false)
      return
    }

    if (vatTreatment === 'incl') {
      setFieldValue('price', roundMoney(entered / (1 + vatPercent)), true)
      setFieldValue('priceWithVat', roundMoney(entered), false)
    } else {
      setFieldValue('price', roundMoney(entered), true)
      setFieldValue('priceWithVat', roundMoney(entered * (1 + vatPercent)), false)
    }
  }

  const changeVatTreatment = (next: VatTreatment) => {
    setVatTreatment(next)
    setPriceText(formatMoneyText(next === 'incl' ? values.priceWithVat : values.price))
  }

  // Reschedule fee is local-only (see state declarations above) - mirrors the
  // same excl/incl VAT pair handling as the real price field, just not saved.
  const handleRescheduleFeeChange = (raw: string) => {
    const text = sanitizeMoneyText(raw)
    setRescheduleFeeText(text)

    const entered = parseMoneyText(text)
    if (entered === null) {
      setRescheduleFeeExcl(undefined)
      setRescheduleFeeIncl(undefined)
      return
    }

    if (rescheduleVat === 'incl') {
      setRescheduleFeeExcl(roundMoney(entered / (1 + vatPercent)))
      setRescheduleFeeIncl(roundMoney(entered))
    } else {
      setRescheduleFeeExcl(roundMoney(entered))
      setRescheduleFeeIncl(roundMoney(entered * (1 + vatPercent)))
    }
  }

  const changeRescheduleVat = (next: VatTreatment) => {
    setRescheduleVat(next)
    setRescheduleFeeText(formatMoneyText(next === 'incl' ? rescheduleFeeIncl : rescheduleFeeExcl))
  }

  const canSave =
    isParentReady &&
    isValid &&
    values.nameEn.trim().length > 0 &&
    values.nameAr.trim().length > 0 &&
    (isB8akService || (Boolean(packageWeeks) && Boolean(weeklyVisits))) &&
    !isLoading &&
    !isLoadingConfig &&
    !isSaving

  const editedName = isArabic ? initialNameAr || initialNameEn : initialNameEn || initialNameAr
  const title = isEdit
    ? `${t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.EDIT_SUB_SERVICE_TITLE')}${editedName ? ` — ${editedName}` : ''
    }`
    : t(
      serviceLevel === 3
        ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADD_SUB_SUB_SERVICE'
        : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADD_SUB_SERVICE'
    )

  return (
    <>
    <Modal
      show={isShow}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
      dialogClassName='service-form__dialog'
      contentClassName='service-form__content'
    >
      <div className='service-form'>
        <div className='service-form__header'>
          <h2 className='service-form__title'>{title}</h2>
          <button
            type='button'
            className='service-form__close'
            aria-label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CLOSE')}
            onClick={onHide}
          >
            <KTSVG defaultColor={true} path={FORM_ICONS.close} />
          </button>
        </div>

        <div
          className={`service-form__body${isLoadingConfig ? ' service-form__body--loading' : ''}`}
        >
          {isLoadingConfig && (
            <div className='service-form__loading' role='status' aria-live='polite'>
              <span className='spinner-border' aria-hidden='true' />
              <span className='service-form__loading-text'>{t('COMMON.LOADING')}</span>
            </div>
          )}

          <div className='service-form__banner'>
            <span className='service-form__banner-icon'>
              <KTSVG defaultColor={true} path={FORM_ICONS.info} />
            </span>
            <p className='service-form__banner-text'>
              {t(
                isB8akService
                  ? 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.B8AK_SERVICE_NOTICE'
                  : 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CUSTOM_SERVICE_NOTICE'
              )}
            </p>
          </div>



          <fieldset className='service-form__fieldset'>
          <div className='service-form__grid'>
            <TextField
              id='sub-service-name-en'
              disabled={isB8akService || isRemovedFromB8ak}
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ENGLISH')}
              placeholder={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_EN_PLACEHOLDER')}
              required
              value={values.nameEn}
              error={touched.nameEn ? (errors.nameEn as string | undefined) : undefined}
              onChange={(value) => setFieldValue('nameEn', value, true)}
              onBlur={() => setFieldTouched('nameEn', true)}
            />
            <TextField
              id='sub-service-name-ar'
              disabled={isB8akService || isRemovedFromB8ak}
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ARABIC')}
              placeholder={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SERVICE_NAME_AR_PLACEHOLDER')}
              required
              value={values.nameAr}
              error={touched.nameAr ? (errors.nameAr as string | undefined) : undefined}
              onChange={(value) => setFieldValue('nameAr', value, true)}
              onBlur={() => setFieldTouched('nameAr', true)}
            />

            <TextAreaField
              id='sub-service-desc-en'
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DESCRIPTION')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ENGLISH')}
              value={values.subTitleTextEn || ''}
              onChange={(value) => setFieldValue('subTitleTextEn', value)}
            />
            <TextAreaField
              id='sub-service-desc-ar'
              label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DESCRIPTION')}
              labelHint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ARABIC')}
              value={values.subTitleTextAr || ''}
              onChange={(value) => setFieldValue('subTitleTextAr', value)}
            />
          </div>
          </fieldset>

          {/* A service removed from B8ak keeps only its description editable. */}
          <fieldset className='service-form__fieldset' disabled={isRemovedFromB8ak}>
          <div className='service-form__grid' style={{marginTop: '16px'}}>

              <TextField
                id='sub-service-duration'
                disabled={isB8akService}
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DURATION')}
                placeholder={durationPlaceholder}
                suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MINS')}
                value={toFieldText(
                  values.visitDuration ?? (isB8akService ? catalogueDuration : undefined)
                )}
                hint={durationHint}
                error={errors.visitDuration as string | undefined}
                onChange={(value) => setFieldValue('visitDuration', toOptionalNumber(value), true)}
              />
              <TextField
                id='sub-service-workers'
                disabled={isB8akService}
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WORKERS_NEEDED')}
                placeholder={isB8akService ? undefined : '1'}
                value={toFieldText(
                  values.numberOfWorkers ??
                    (isB8akService
                      ? catalogueWorkersNeeded ?? (showMaintenanceDefaults ? 1 : undefined)
                      : undefined)
                )}
                error={errors.numberOfWorkers as string | undefined}
                onChange={(value) =>
                  setFieldValue('numberOfWorkers', toOptionalNumber(value), true)
                }
              />

              <SelectField
                id='sub-service-shift'
                fullRow={false}
                disabled={isB8akService}
                label={t('SERVICE_MANAGEMENT_PAGE.PACKAGE.SHIFT')}
                placeholder={t('SERVICE_MANAGEMENT_PAGE.PACKAGE.ALL')}
                value={String(shiftType)}
                options={[
                  {value: '0', label: t('SERVICE_MANAGEMENT_PAGE.PACKAGE.ALL')},
                  {value: '1', label: t('SERVICE_MANAGEMENT_PAGE.PACKAGE.MORNING')},
                  {value: '2', label: t('SERVICE_MANAGEMENT_PAGE.PACKAGE.EVENING')},
                ]}
                onChange={(value) => setShiftType(Number(value))}
              />
              <TextField
                id='sub-service-minimum-charge'
                disabled
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MINIMUM_CHARGE')}
                suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')}
                hint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MINIMUM_CHARGE_HINT')}
                value=''
                onChange={() => undefined}
              />

              <VatTreatmentField
                value={vatTreatment}
                disabled
                onChange={changeVatTreatment}
              />
              <TextField
                id='sub-service-price'
                disabled={isB8akService}
                label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRICE')}
                placeholder='80'
                suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')}
                hint={priceText === 'incl' ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_HINT', { vat : vatPercent *100}) : t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_EXCL_HINT' , { vat : vatPercent *100})  }
                value={priceText}
                error={errors.price as string | undefined}
                onChange={handlePriceChange}
              />
            </div>
          </fieldset>

          {/* Outside any disabling fieldset so the header stays clickable; a service removed
              from B8ak still locks the fields once expanded, same as the fieldset above did. */}
          <CollapsibleSection
            title={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MAKE_IT_RECURRING')}
            badge={
              (packageWeeks ?? 1) <= 1 && (weeklyVisits ?? 1) <= 1
                ? t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SINGLE_VISIT_BY_DEFAULT')
                : ''
            }
            expanded={isRecurringOpen}
            disabled={isRemovedFromB8ak}
            onToggle={() => setIsRecurringOpen((current) => !current)}
          >
              <div className={isCustom ? undefined : 'service-form__section-body--locked'}>
                {!isCustom && (
                  <div className='service-form__inline-banner'>
                    <span className='service-form__inline-banner-icon'>
                      <KTSVG defaultColor={true} path={FORM_ICONS.info} />
                    </span>
                    <p className='service-form__inline-banner-text'>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RECURRING_LOCKED_NOTICE')}
                    </p>
                  </div>
                )}
                <div className='service-form__grid'>
                  <TextField
                    id='sub-service-package-weeks'
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PACKAGE_WEEKS')}
                    required={isCustom}
                    disabled={!isCustom}
                    compact
                    icon={FORM_ICONS.clock}
                    value={
                      packageWeeks === undefined
                        ? showMaintenanceDefaults
                          ? '1'
                          : ''
                        : String(packageWeeks)
                    }
                    onChange={(value) =>
                      setPackageWeeks(
                        value.trim() === '' ? undefined : Number(value.replace(/[^0-9]/g, '')) || 0
                      )
                    }
                  />
                  <TextField
                    id='sub-service-weekly-visits'
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.WEEKLY_VISITS')}
                    required={isCustom}
                    disabled={!isCustom}
                    compact
                    icon={FORM_ICONS.repeat}
                    value={
                      weeklyVisits === undefined
                        ? showMaintenanceDefaults
                          ? '1'
                          : ''
                        : String(weeklyVisits)
                    }
                    onChange={(value) =>
                      setWeeklyVisits(
                        value.trim() === '' ? undefined : Number(value.replace(/[^0-9]/g, '')) || 0
                      )
                    }
                  />
                </div>
                <p className='service-form__section-note'>
                  <span className='service-form__section-note-icon'>
                    <KTSVG defaultColor={true} path={FORM_ICONS.info} />
                  </span>
                  {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RECURRING_NOTE')}
                </p>
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULING')}
              badge={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.PRE_FILLED_BY_DEFAULT')}
              expanded={isReschedulingOpen}
              disabled={isRemovedFromB8ak}
              onToggle={() => setIsReschedulingOpen((current) => !current)}
            >
              <div className={isCustom ? undefined : 'service-form__section-body--locked'}>
                {!isCustom && (
                  <div className='service-form__inline-banner'>
                    <span className='service-form__inline-banner-icon'>
                      <KTSVG defaultColor={true} path={FORM_ICONS.info} />
                    </span>
                    <p className='service-form__inline-banner-text'>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULING_LOCKED_NOTICE')}
                    </p>
                  </div>
                )}
                <div className='service-form__grid service-form__grid--thirds'>
                  <TextField
                    id='sub-service-reschedule-window'
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULE_WINDOW')}
                    suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.DAYS')}
                    disabled={!isCustom}
                    value={rescheduleWindowDays === undefined ? '' : String(rescheduleWindowDays)}
                    onChange={(value) =>
                      setRescheduleWindowDays(
                        value.trim() === '' ? undefined : Number(value.replace(/[^0-9]/g, '')) || 0
                      )
                    }
                  />
                  <TextField
                    id='sub-service-reschedule-cutoff'
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULE_CUT_OFF')}
                    suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.MINS')}
                    disabled={!isCustom}
                    value={rescheduleBeforeMinutes === undefined ? '' : String(rescheduleBeforeMinutes)}
                    onChange={(value) =>
                      setRescheduleBeforeMinutes(
                        value.trim() === '' ? undefined : Number(value.replace(/[^0-9]/g, '')) || 0
                      )
                    }
                  />
                  <TextField
                    id='sub-service-reschedule-limit'
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULE_LIMIT')}
                    disabled={!isCustom}
                    value={rescheduleLimit === undefined ? '' : String(rescheduleLimit)}
                    onChange={(value) =>
                      setRescheduleLimit(
                        value.trim() === '' ? undefined : Number(value.replace(/[^0-9]/g, '')) || 0
                      )
                    }
                  />
                </div>

                <ToggleRow
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ONE_TIME_ALL_DAYS')}
                  checked={oneTimeForAllDays}
                  disabled={!isCustom}
                  onChange={() => setOneTimeForAllDays((current) => !current)}
                >
                  {!isCustom && (
                    <span className='service-form__coming-soon'>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.COMING_SOON')}
                    </span>
                  )}
                </ToggleRow>
                <ToggleRow
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CHARGE_RESCHEDULE_FEE')}
                  checked={enableRescheduling}
                  disabled={!isCustom}
                  onChange={() => setEnableRescheduling((current) => !current)}
                >
                  {!isCustom && (
                    <span className='service-form__coming-soon'>
                      {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.COMING_SOON')}
                    </span>
                  )}
                </ToggleRow>

                {enableRescheduling && (
                  <div className='service-form__grid'>
                    <VatTreatmentField
                      value={rescheduleVat}
                      disabled={!isCustom}
                      onChange={changeRescheduleVat}
                    />
                    <TextField
                      id='sub-service-reschedule-fee'
                      label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RESCHEDULE_FEE')}
                      placeholder='80'
                      suffix={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAR')}
                      hint={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_HINT')}
                      disabled={!isCustom}
                      value={rescheduleFeeText}
                      onChange={handleRescheduleFeeChange}
                    />
                  </div>
                )}
              </div>
            </CollapsibleSection>

          <fieldset className='service-form__fieldset' disabled={isRemovedFromB8ak}>
            <div className='service-form__field' style={{ marginTop: '18px' }}>
              <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VISIBILITY_CHANNELS')} />
              <div className='service-form__visibility'>
                <ToggleRow
                  label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ACTIVE')}
                  checked={Boolean(values.status)}
                  locked={Boolean(statusLockMessage)}
                  onChange={() => {
                    if (statusLockMessage) {
                      toast.warning(statusLockMessage, {
                        ...ToastDefaultConfig(),
                        toastId: statusLockMessage,
                      })
                      return
                    }
                    setFieldValue('status', values.status ? 0 : 1)
                  }}
                />
                {/* B8ak services only; saved with the service config. */}
                {!isCustom && (
                  <ToggleRow
                    label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.RECEIVE_B8AK_ORDERS')}
                    checked={receiveB8akOrders}
                    // A service no longer in the B8ak catalogue keeps its current value
                    disabled={isRemovedFromB8ak}
                    onChange={() => setReceiveB8akOrders((current) => !current)}
                  >
                    <span className='service-form__info-badge'>
                      <KTSVG defaultColor={true} path={FORM_ICONS.infoBadge} />
                    </span>
                  </ToggleRow>
                )}
              </div>
            </div>
          </fieldset>
        </div>

        <div className='service-form__footer'>
          <button
            type='button'
            className='service-form__btn service-form__btn--cancel'
            onClick={onHide}
            disabled={isLoading || isSaving}
          >
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.CANCEL')}
          </button>
          <button
            type='button'
            className='service-form__btn service-form__btn--secondary'
            disabled={isRemovedFromB8ak}
            onClick={() => setIsAdvancedPricingOpen(true)}
          >
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.ADVANCED_PRICING')}
          </button>
          <button
            type='button'
            className='service-form__btn service-form__btn--save'
            disabled={!canSave}
            onClick={submitForm}
          >
            {isSaving && (
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              />
            )}
            {t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.SAVE')}
          </button>
        </div>
      </div>
    </Modal>
    <AdvancedPricingSettingsModal
      isShow={isAdvancedPricingOpen}
      onHide={() => setIsAdvancedPricingOpen(false)}
      isB8akService={isB8akService}
      vatTreatment={vatTreatment}
      value={cityPrices}
      onSave={setCityPrices}
    />
    </>
  )
}

export default AddSubServiceModal
